import React, { useState } from 'react';
import { 
  Users, UserPlus, FileSpreadsheet, Download, Upload, 
  CheckCircle, Search, Filter, Trash2, Eye, Shield, FileText, Lock, AlertTriangle, CheckCircle2,
  Grid, Sparkles, ShieldCheck, Activity, CreditCard, TrendingUp, BarChart2, Key, Copy, Check
} from 'lucide-react';
import { ModuleExplorer } from './ModuleExplorer';
import { UserSession } from '../AppShell';
import { DEMO_STUDENTS, DemoStudent } from '../data/demoStudents';
import { StudentClinicalAnalysisModal } from './StudentClinicalAnalysisModal';

interface TenantAdminDashboardProps {
  isSubscriptionActive?: boolean;
  currentUser?: UserSession | null;
}

export const TenantAdminDashboard: React.FC<TenantAdminDashboardProps> = ({ isSubscriptionActive = true, currentUser }) => {
  const [students, setStudents] = useState<DemoStudent[]>(DEMO_STUDENTS);
  const [dashboardTab, setDashboardTab] = useState<'modules' | 'roster' | 'add_single' | 'bulk_upload' | 'subscription'>('modules');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentForModal, setSelectedStudentForModal] = useState<DemoStudent | null>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Single Add State with Student Login Credentials
  const [singleName, setSingleName] = useState('');
  const [singleRollId, setSingleRollId] = useState('');
  const [singleGrade, setSingleGrade] = useState('Class 3-A');
  const [singleCategory, setSingleCategory] = useState('Autism Spectrum (ASD)');
  const [singleGuardianName, setSingleGuardianName] = useState('');
  const [singleGuardianPhone, setSingleGuardianPhone] = useState('');
  const [singleTherapist, setSingleTherapist] = useState('Sarah Jenkins (SLP)');
  const [singleStudentEmail, setSingleStudentEmail] = useState('');
  const [singleStudentPassword, setSingleStudentPassword] = useState('');
  const [singlePortalPin, setSinglePortalPin] = useState('1234');

  // Bulk Upload State
  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [parsedPreview, setParsedPreview] = useState<any[]>([]);

  if (!isSubscriptionActive) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="bg-white rounded-3xl border border-red-200 shadow-2xl p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
            <Lock className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full uppercase tracking-wider border border-red-300">
              Active Subscription Required
            </span>
            <h2 className="text-3xl font-black text-slate-900">Tenant Subscription Inactive</h2>
          </div>
        </div>
      </div>
    );
  }

  const handleOpenAnalysis = (st: DemoStudent) => {
    setSelectedStudentForModal(st);
    setIsAnalysisModalOpen(true);
  };

  const handleCopyCredentials = (st: DemoStudent) => {
    const text = `Student Login Credentials for ${st.name}:\nEmail: ${st.studentEmail}\nPassword: ${st.studentPassword || 'Student#2026'}\nPortal PIN: ${st.portalPin || '1234'}\nPortal URL: https://abilityos.bugfreesolution.com`;
    navigator.clipboard.writeText(text);
    setCopiedId(st.id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleAddSingleStudent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleName || !singleGuardianName) return;

    const email = singleStudentEmail || `${singleName.toLowerCase().replace(/\s+/g, '.')}@bugfreeschool.org`;
    const pwd = singleStudentPassword || `${singleName.split(' ')[0]}#2026Password`;

    const newStudent: DemoStudent = {
      id: Date.now().toString(),
      name: singleName,
      rollId: singleRollId || `STU-2026-00${students.length + 1}`,
      age: 8,
      grade: singleGrade,
      disabilityCategory: singleCategory,
      guardianName: singleGuardianName,
      guardianPhone: singleGuardianPhone || '+91 98000 00000',
      therapist: singleTherapist,
      studentEmail: email,
      studentPassword: pwd,
      portalPin: singlePortalPin || '1234',
      iepStatus: 'ACTIVE',
      iepGoal: 'New SMART IEP Goal assigned upon enrolment',
      recentScore: 'Initial Assessment Pending',
      overallMasteryPercent: 75,
      clinicalSummary: 'Newly enrolled student profile with inclusive login credentials.',
      aiRecommendation: 'Conduct initial diagnostic assessment in DiagnosticAssessmentModule.',
      moduleMasteryMap: {}
    };

    setStudents([newStudent, ...students]);
    setSingleName('');
    setSingleRollId('');
    setSingleGuardianName('');
    setSingleGuardianPhone('');
    setSingleStudentEmail('');
    setSingleStudentPassword('');
    setDashboardTab('roster');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);

      const mockParsed = [
        { name: 'Miss Diya Sengupta', rollId: 'STU-2026-101', grade: 'Class 1-A', category: 'Hearing Impairment', guardian: 'Rohan Sengupta', phone: '+91 98450 11990', therapist: 'Sarah Jenkins (SLP)', email: 'diya.sengupta@bugfreeschool.org', pwd: 'Diya#2026Password', pin: '4567' },
        { name: 'Master Ishan Verma', rollId: 'STU-2026-102', grade: 'Class 3-B', category: 'Down Syndrome', guardian: 'Meera Verma', phone: '+91 98765 43210', therapist: 'Michael Chen (OT)', email: 'ishan.verma@bugfreeschool.org', pwd: 'Ishan#2026Password', pin: '5678' },
        { name: 'Master Tanmay Roy', rollId: 'STU-2026-103', grade: 'Class 6-A', category: 'Visual Impairment (Braille)', guardian: 'Kunal Roy', phone: '+91 98112 23344', therapist: 'Priya Nair (PT)', email: 'tanmay.roy@bugfreeschool.org', pwd: 'Tanmay#2026Password', pin: '6789' },
      ];
      setParsedPreview(mockParsed);
    }
  };

  const handleConfirmBulkUpload = () => {
    if (parsedPreview.length === 0) return;

    const uploadedStudents: DemoStudent[] = parsedPreview.map((item, idx) => ({
      id: (Date.now() + idx).toString(),
      name: item.name,
      rollId: item.rollId,
      age: 9,
      grade: item.grade,
      disabilityCategory: item.category,
      guardianName: item.guardian,
      guardianPhone: item.phone,
      therapist: item.therapist,
      studentEmail: item.email,
      studentPassword: item.pwd,
      portalPin: item.pin,
      iepStatus: 'ACTIVE',
      iepGoal: 'Bulk imported IEP goal milestone',
      recentScore: '85% Initial Score',
      overallMasteryPercent: 80,
      clinicalSummary: 'Bulk imported student roster record with student login credentials.',
      aiRecommendation: 'Initialize baseline therapy goals.',
      moduleMasteryMap: {}
    }));

    setStudents([...uploadedStudents, ...students]);
    setUploadSuccess(`Successfully imported ${parsedPreview.length} students with inclusive login credentials from "${file?.name}"!`);
    setFile(null);
    setParsedPreview([]);
    setDashboardTab('roster');

    setTimeout(() => setUploadSuccess(''), 4000);
  };

  const filteredStudents = students.filter((s) => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.rollId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.disabilityCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.studentEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Admin Hero Workspace Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-6 rounded-3xl shadow-xl border border-blue-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-2 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-extrabold rounded-full border border-emerald-500/30 uppercase tracking-wider">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Admin Workspace • {currentUser?.tenant || 'BugFree Solutions Demo Organization'}</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black">
            Welcome, {currentUser?.name || 'Demo Admin User'}!
          </h1>
          <p className="text-xs text-blue-200 max-w-2xl">
            Logged in as <strong className="text-white font-mono">{currentUser?.email || 'demo@bugfreesolution.com'}</strong>. Full authorization granted for <span className="text-amber-300 font-bold">All 30 AbilityOS Modules & Student Analytics</span>.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white/10 p-3 rounded-2xl border border-white/10 backdrop-blur-md shrink-0">
          <div className="text-center px-3 border-r border-white/10">
            <div className="text-xl font-black text-amber-300 font-mono">30 / 30</div>
            <div className="text-[10px] text-blue-200">Modules Active</div>
          </div>
          <div className="text-center px-3">
            <div className="text-xl font-black text-emerald-400 font-mono">{students.length}</div>
            <div className="text-[10px] text-blue-200">Demo Students</div>
          </div>
        </div>
      </div>

      {/* Admin Workspace Tab Navigation */}
      <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setDashboardTab('modules')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              dashboardTab === 'modules' 
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Grid className="w-4 h-4 text-amber-400" />
            <span>🚀 30-Module Suite (Interactive Launcher)</span>
          </button>

          <button
            onClick={() => setDashboardTab('roster')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              dashboardTab === 'roster' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Users className="w-4 h-4 text-blue-600" />
            <span>Student Roster & Login Credentials ({students.length})</span>
          </button>

          <button
            onClick={() => setDashboardTab('add_single')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              dashboardTab === 'add_single' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            <span>Enrol Single Student</span>
          </button>

          <button
            onClick={() => setDashboardTab('bulk_upload')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              dashboardTab === 'bulk_upload' 
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>Bulk Upload Excel</span>
          </button>
        </div>
      </div>

      {uploadSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          {uploadSuccess}
        </div>
      )}

      {/* VIEW 1: PROMINENT 30-MODULE SUITE LAUNCHER */}
      {dashboardTab === 'modules' && (
        <div className="space-y-4">
          <ModuleExplorer currentUser={currentUser} />
        </div>
      )}

      {/* VIEW 2: STUDENT ROSTER TABLE WITH LOGIN CREDENTIALS & 30-MODULE CLINICAL ANALYSIS TRIGGER */}
      {dashboardTab === 'roster' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search student, email, Roll ID, category..."
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div className="text-xs text-slate-500 font-semibold">
              Showing {filteredStudents.length} of {students.length} students
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-extrabold uppercase text-slate-600">
                  <th className="p-3">Roll ID</th>
                  <th className="p-3">Student Name</th>
                  <th className="p-3">Student Portal Login Credentials</th>
                  <th className="p-3">Disability Category</th>
                  <th className="p-3">Mastery Index</th>
                  <th className="p-3">Clinical Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredStudents.map((st) => (
                  <tr key={st.id} className="hover:bg-slate-50 transition">
                    <td className="p-3 font-mono font-bold text-slate-700">{st.rollId}</td>
                    <td className="p-3">
                      <div className="font-bold text-slate-900">{st.name}</div>
                      <div className="text-[10px] text-slate-500 font-medium">{st.grade} • Guardian: {st.guardianName}</div>
                    </td>

                    {/* Student Login Credentials Column */}
                    <td className="p-3 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Key className="w-3.5 h-3.5 text-amber-500" />
                        <span className="font-mono text-blue-700 font-bold text-[11px]">{st.studentEmail}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-600">
                        <span>Pwd: <strong className="font-mono text-slate-800">{st.studentPassword || 'Student#2026'}</strong></span>
                        <span>PIN: <strong className="font-mono text-emerald-700">{st.portalPin || '1234'}</strong></span>
                        <button
                          onClick={() => handleCopyCredentials(st)}
                          className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-bold transition flex items-center gap-1 text-[9px]"
                        >
                          {copiedId === st.id ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-blue-600" />}
                          <span>{copiedId === st.id ? 'Copied!' : 'Copy'}</span>
                        </button>
                      </div>
                    </td>

                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded font-bold text-[10px]">
                        {st.disabilityCategory}
                      </span>
                    </td>
                    <td className="p-3 font-mono font-black text-emerald-700">
                      {st.overallMasteryPercent}%
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleOpenAnalysis(st)}
                        className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-[11px] rounded-xl shadow-sm transition flex items-center gap-1.5"
                      >
                        <BarChart2 className="w-3.5 h-3.5" />
                        <span>30-Module Analysis</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW 3: ENROL SINGLE STUDENT WITH INCLUSIVE LOGIN CREDENTIALS */}
      {dashboardTab === 'add_single' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 max-w-2xl mx-auto space-y-4">
          <h3 className="text-lg font-extrabold text-slate-900 border-b pb-3">Enrol Single Student with Login Credentials</h3>
          
          <form onSubmit={handleAddSingleStudent} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Student Full Name *</label>
                <input
                  type="text" required
                  value={singleName} onChange={(e) => setSingleName(e.target.value)}
                  placeholder="Master Aarav Sharma"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Roll / Student ID</label>
                <input
                  type="text"
                  value={singleRollId} onChange={(e) => setSingleRollId(e.target.value)}
                  placeholder="STU-2026-099"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Inclusive Student Login Credentials Fields */}
            <div className="bg-blue-50/70 border border-blue-200 p-4 rounded-2xl space-y-3">
              <div className="text-xs font-extrabold text-blue-900 flex items-center gap-1.5">
                <Key className="w-4 h-4 text-amber-500" />
                <span>Student Mobile App & Web Login Credentials</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Student Portal Email *</label>
                  <input
                    type="email"
                    value={singleStudentEmail} onChange={(e) => setSingleStudentEmail(e.target.value)}
                    placeholder="student.name@bugfreeschool.org"
                    className="w-full px-3 py-2 bg-white border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Portal PIN</label>
                  <input
                    type="text"
                    value={singlePortalPin} onChange={(e) => setSinglePortalPin(e.target.value)}
                    placeholder="1234"
                    className="w-full px-3 py-2 bg-white border rounded-xl text-xs font-mono font-bold focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">Student Portal Password</label>
                <input
                  type="text"
                  value={singleStudentPassword} onChange={(e) => setSingleStudentPassword(e.target.value)}
                  placeholder="Student#2026Password"
                  className="w-full px-3 py-2 bg-white border rounded-xl text-xs font-mono font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Grade / Classroom</label>
                <input
                  type="text"
                  value={singleGrade} onChange={(e) => setSingleGrade(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Disability Category</label>
                <select
                  value={singleCategory} onChange={(e) => setSingleCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Autism Spectrum (ASD)">Autism Spectrum (ASD)</option>
                  <option value="Speech & Articulation Impairment">Speech & Articulation Impairment</option>
                  <option value="Cerebral Palsy (Motor Skills)">Cerebral Palsy (Motor Skills)</option>
                  <option value="Down Syndrome">Down Syndrome</option>
                  <option value="Hearing Impairment">Hearing Impairment</option>
                  <option value="Visual Impairment (Braille)">Visual Impairment (Braille)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Guardian Name *</label>
                <input
                  type="text" required
                  value={singleGuardianName} onChange={(e) => setSingleGuardianName(e.target.value)}
                  placeholder="Parent / Guardian Name"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Guardian Phone</label>
                <input
                  type="text"
                  value={singleGuardianPhone} onChange={(e) => setSingleGuardianPhone(e.target.value)}
                  placeholder="+91 98000 00000"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="pt-3 border-t flex justify-end gap-3">
              <button type="button" onClick={() => setDashboardTab('roster')} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs">Cancel</button>
              <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow">Enrol Student</button>
            </div>
          </form>
        </div>
      )}

      {/* VIEW 4: BULK UPLOAD EXCEL INCLUSIVE OF STUDENT LOGIN CREDENTIALS */}
      {dashboardTab === 'bulk_upload' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 max-w-3xl mx-auto space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <h3 className="text-lg font-extrabold text-slate-900">Batch Bulk Student Excel / CSV Importer</h3>
              <p className="text-xs text-slate-500">Upload Excel spreadsheet containing student profiles AND login credentials (email, password, PIN)</p>
            </div>

            <a
              href="/downloads/abilityos_student_import_template.csv"
              download
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl flex items-center gap-1.5 transition"
            >
              <Download className="w-4 h-4 text-blue-600" />
              <span>Download Inclusive Template</span>
            </a>
          </div>

          <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center space-y-3 bg-slate-50 hover:bg-slate-100/50 transition">
            <Upload className="w-10 h-10 text-blue-600 mx-auto" />
            <div className="text-sm font-bold text-slate-800">
              Drag & Drop your student Excel / CSV file here
            </div>

            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
              className="hidden"
              id="excel-file-input"
            />
            <label
              htmlFor="excel-file-input"
              className="inline-block px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow cursor-pointer transition"
            >
              Select Excel File
            </label>
          </div>

          {/* Parsed Preview Table showing inclusive login credentials */}
          {parsedPreview.length > 0 && (
            <div className="space-y-3 border-t pt-4">
              <h4 className="font-extrabold text-xs text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Parsed Student Roster Preview (Inclusive of Login Credentials):
              </h4>

              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 text-[10px] font-extrabold uppercase text-slate-600">
                    <tr>
                      <th className="p-2">Name</th>
                      <th className="p-2">Roll ID</th>
                      <th className="p-2">Student Email</th>
                      <th className="p-2">Password</th>
                      <th className="p-2">PIN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsedPreview.map((item, i) => (
                      <tr key={i} className="font-medium">
                        <td className="p-2 font-bold text-slate-900">{item.name}</td>
                        <td className="p-2 font-mono text-slate-600">{item.rollId}</td>
                        <td className="p-2 font-mono text-blue-700 font-bold">{item.email}</td>
                        <td className="p-2 font-mono text-slate-700">{item.pwd}</td>
                        <td className="p-2 font-mono text-emerald-700 font-bold">{item.pin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={handleConfirmBulkUpload}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow transition"
              >
                Confirm & Import {parsedPreview.length} Students with Login Credentials
              </button>
            </div>
          )}

        </div>
      )}

      {/* Individual Student 30-Module Clinical Analysis Modal */}
      <StudentClinicalAnalysisModal 
        student={selectedStudentForModal}
        isOpen={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        onSelectModule={() => setDashboardTab('modules')}
      />

    </div>
  );
};
